import { DiagramModel } from "@projectstorm/react-diagrams";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector, useStore } from "react-redux";
import MainWindow from "./MainWindow";
import Context from "../store/context";
import { proxyAPI } from "../proxyAPI";
import {
  addNewWorkflow,
  closeProject,
  createNewMessageConfig,
  editAction,
  saveProject,
  selectProjectUUID,
  selectWorkflows,
  setProjectData,
} from "../store/slices/projectSlice";
import { setSendLists } from "../store/slices/sendListsSlice";
import { createEditorState, makeid, shortUUID } from "../helpers";
import { setSurveyThemes } from "../store/slices/surveyThemesSlice";
import FullScreenLoadingIndicator from "../Common/FullScreenLoadingIndicator";
import { useGetContactFieldsQuery } from "../store/slices/contactsSlice";
import {
  useGetCommsSettingsDataQuery,
  useGetSystemDataStructuresQuery,
} from "../store/slices/settingsSlice";

// page props
const { productName } = JSON.parse(
  document.querySelector("#app").dataset.page
).props;

// single workflow
const newWorkflow = (IsEntrypoint = false) => ({
  Label: "New workflow",
  UUID: uuidv4(),
  IsEntrypoint,
  EventListeners: [],
});

const getActionUuidFromPortName = (portName) => {
  if (!portName) return null;
  // check pipe
  if (portName.includes("|")) {
    return portName.split("|")[1];
  }
  // check length for old versions - both have long uuid
  if (portName.length === 73) {
    return portName.slice(37);
  }
  // if 71 both from backend - both have short uuid
  if (portName.length === 71) {
    return portName.slice(36);
  }
  //if 72 check last two character
  if (portName.length === 72) {
    if (portName.slice(-2) === "-s") {
      // second part is short uuid with -s at the end
      return portName.slice(37);
    } else {
      // first part is short
      return portName.slice(36);
    }
  }
};

export default function AdvancedBuilder({ engine, dagreEngine }) {
  const { data: commsSettings } = useGetCommsSettingsDataQuery();

  // new project
  const newProjectData = () => {
    const newMessageConfigUUID = uuidv4();

    return {
      Label: "New Project",
      UUID: shortUUID(),
      Status: "draft",
      Reference: makeid(8),
      Workflows: [newWorkflow(true)],
      EventHandlers: [],
      BusinessHours: [],
      MessageTemplates: [],
      InputArguments: [],
      MessageConfigUUID: newMessageConfigUUID,
      MessageConfigs: [
        createNewMessageConfig({
          UUID: newMessageConfigUUID,
          Label: "Default message config",
          commsSettings,
        }),
      ],
    };
  };

  // inertia props
  const params = useParams();

  const dispatch = useDispatch();
  const store = useStore();
  const navigate = useNavigate();
  const zoomedInNode = useRef(null);

  // global state (to be passed in context)
  const {
    data: contactFields = [],
    isFetching,
    isLoading,
  } = useGetContactFieldsQuery();
  const [reportingTopics, setreportingTopics] = useState([]);
  const [reportingTemplates, setReportingTemplates] = useState([]);
  const [apiProfiles, setApiProfiles] = useState([]);
  const [components, setComponents] = useState([]);
  const [jsonCollections, setJsonCollections] = useState([]);
  const [isModalSidebar, setIsModalSidebar] = useState(false);

  // workflows data
  const projectUUID = useSelector(selectProjectUUID);
  const [projectLoading, setProjectLoading] = useState(false);

  const save = useCallback(
    () => dispatch(saveProject({ navigate, engine })),
    [dispatch, engine, navigate]
  );

  // zoom to fit handler
  const zoomToFit = useCallback(() => {
    engine.zoomToFitNodes({ margin: 10, maxZoom: 1 });
  }, [engine]);

  // autoarrange handler
  const autoArrange = useCallback(() => {
    dagreEngine.redistribute(engine.model);
    zoomToFit();
  }, [dagreEngine, engine, zoomToFit]);

  const registerDiagramLinkListener = useCallback(
    (model) => {
      model.registerListener({
        linksUpdated: (event) => {
          event.link.registerListener({
            targetPortChanged: (e) => {
              const targetUUID =
                e.entity.targetPort?.parent?.options?.id ?? null;
              const actionUUID = getActionUuidFromPortName(
                e.entity.sourcePort?.options?.name
              );

              if (!actionUUID) {
                return;
              }

              dispatch(
                editAction({
                  actionUUID,
                  field: "TypeUUID",
                  value: targetUUID,
                })
              );
            },
          });
        },
      });
    },
    [dispatch]
  );

  // add a child node to a parent node
  // if no parent, add the node to the canvas
  const addChild = useCallback(
    (data, parentUUID) => {
      const childData = data ?? { workflow: newWorkflow() };

      dispatch(addNewWorkflow(childData.workflow));
      // create the node model using the factory
      const newNode = engine.nodeFactories
        .getFactory("logic")
        .generateModel(childData);
      // add the node to the model
      engine.model.addNode(newNode);
      // if there is a parent node, create a link
      newNode.setSelected();
      if (parentUUID) {
        const parentNode = engine.model.getNode(parentUUID);
        newNode.setPosition(
          parentNode.position.x + 1000,
          parentNode.position.y
        );
      }
      // show the available nodes that can connect the new node
      engine.model.clearSelection();
      engine.repaintCanvas();
      setTimeout(() => {
        zoomToFit();
      }, 100);
      return newNode;
    },
    [dispatch, engine, zoomToFit]
  );

  // initialize project data
  const initializeWorkflowNodes = useCallback(
    (workflows) => {
      if (workflows.length > 0) {
        const model = new DiagramModel();
        registerDiagramLinkListener(model);
        engine.setModel(model);
        setTimeout(() => {
          workflows.forEach((workflow) =>
            addChild({
              workflow,
            })
          );
          setTimeout(() => {
            autoArrange();
          }, 200);
        }, 100);
      }
    },
    [addChild, autoArrange, engine, registerDiagramLinkListener]
  );

  const resetLayout = useCallback(() => {
    const projectWorkflows = selectWorkflows(store.getState());
    initializeWorkflowNodes(projectWorkflows);
  }, [initializeWorkflowNodes, store]);

  // double click to zoom handler
  const zoomToSelected = useCallback(() => {
    const [selectedNode] = engine.model.getSelectedEntities();

    if (
      zoomedInNode.current === null ||
      zoomedInNode.current !== selectedNode
    ) {
      engine.zoomToFitSelectedNodes({ margin: 10, maxZoom: 2 });
      zoomedInNode.current = selectedNode;
    } else {
      engine.zoomToFitNodes({ margin: 10, maxZoom: 1 });
      zoomedInNode.current = null;
    }
  }, [engine]);

  // project loader
  const projectLoader = useCallback(async () => {
    setProjectLoading(true);
    let projectData = null;

    if (params.projectID === undefined) {
      setProjectLoading(false);
      return null;
    }

    // new project
    if (params.projectID === "new") {
      if (commsSettings) {
        projectData = newProjectData();
      }
    } else {
      // get project data
      projectData = await proxyAPI({
        prefix: "logic/workflows/api",
        url: `/project/${params.projectID}`,
        setLoading: () => null,
      });
    }

    // get themes
    const themes = await proxyAPI({
      prefix: "logic/workflows/api",
      url: `/themes`,
      setLoading: () => null,
    });

    // get send lists for templates and workflows
    const sendLists = await Promise.all(
      [
        ...(projectData?.MessageTemplates ?? []),
        ...(projectData?.Workflows || []),
      ]
        .filter((mt) => mt.ContactSetUUID === "yes")
        .map((mt) =>
          proxyAPI({ prefix: "contacts/v1", url: `/send-list/${mt.UUID}` })
        )
    ).then((values) => values.filter((v) => v));
    setTimeout(() => {
      setProjectLoading(false);
    }, 400);
    return { projectData, themes, sendLists };
  }, [commsSettings, params.projectID]);

  // load project
  useEffect(() => {
    if (!params.projectID) {
      dispatch(closeProject());
      return;
    }
    if (params.projectID === "new" && !commsSettings) {
      return;
    }
    if (params.projectID) {
      let isCurrentLoad = true;
      (async () => {
        const loaderData = await projectLoader();

        if (!isCurrentLoad || !loaderData?.projectData) {
          setProjectLoading(false);
          return;
        }

        dispatch(setProjectData(loaderData.projectData));
        dispatch(setSendLists(loaderData.sendLists));
        dispatch(setSurveyThemes(loaderData.themes));
        if (loaderData.projectData.ComponentConfig?.Layout) {
          const newModel = new DiagramModel();
          newModel.deserializeModel(
            loaderData.projectData.ComponentConfig.Layout,
            engine
          );
          registerDiagramLinkListener(newModel);
          engine.setModel(newModel);
          requestAnimationFrame(() => {
            engine.repaintCanvas();
          });
        } else {
          initializeWorkflowNodes(loaderData.projectData.Workflows);
        }
      })();
      return () => {
        isCurrentLoad = false;
        dispatch(closeProject());
      };
    }
  }, [
    commsSettings,
    dispatch,
    engine,
    initializeWorkflowNodes,
    params.projectID,
    projectLoader,
    registerDiagramLinkListener,
  ]);

  // update contactfields
  useEffect(() => {
    if (params.workflowUUID) {
      proxyAPI({
        prefix: "logic/reporting",
        url: "/topics?pagination.perPage=1000",
      }).then((data) => setreportingTopics(data.data));
      proxyAPI({
        prefix: "logic/reporting",
        url: "/templates?pagination.perPage=1000",
      }).then((data) => setReportingTemplates(data.data));
      proxyAPI({
        prefix: "logic/workflows/api",
        url: `api-profiles`,
      }).then((data) => {
        if (data?.profiles) {
          setApiProfiles(data.profiles);
        }
      });
      proxyAPI({
        prefix: "logic/workflows/api",
        url: `json-api-data-collections`,
      }).then((data) => {
        if (data?.collections) {
          setJsonCollections(data.collections);
        }
      });
      proxyAPI({
        prefix: "logic/workflows/api",
        url: `list-projects?withParams=true&offset=0&limit=100`,
      }).then((data) => {
        if (data?.projects) {
          setComponents(data.projects);
        }
      });
    }
  }, [params.workflowUUID]);

  const { data: systemDataStructures } = useGetSystemDataStructuresQuery();

  const contextValue = useMemo(
    () => ({
      addChild,
      engine,
      isModalSidebar,
      setIsModalSidebar,
      saveProject: save,
      contactFields,
      reportingTopics,
      reportingTemplates,
      apiProfiles,
      jsonCollections,
      systemDataStructures,
      components,
    }),
    [
      addChild,
      apiProfiles,
      components,
      contactFields,
      engine,
      isModalSidebar,
      jsonCollections,
      reportingTemplates,
      reportingTopics,
      save,
      systemDataStructures,
    ]
  );

  return (
    <>
      <FullScreenLoadingIndicator loading={projectLoading} />
      {projectUUID ? (
        <Context.Provider value={contextValue}>
          <MainWindow
            addChild={addChild}
            engine={engine}
            redistribute={autoArrange}
            saveProject={save}
            zoomToFit={zoomToFit}
            zoomToSelected={zoomToSelected}
            resetLayout={resetLayout}
          />
        </Context.Provider>
      ) : (
        <div className="alert alert-warning text-center container my-5">
          Project not found.{" "}
          <Link to="/library/logic/builder/new/settings">Create new</Link>
        </div>
      )}
    </>
  );
}
